import {ChevronRight, Settings, X, Lock, FileText, LogOut, User} from 'lucide-react'
import React, {useEffect, useRef, useState, useCallback} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import styled from 'styled-components'
import {useGetOneAgencyQuery} from '~/src/store/apis/agency-api'
import {useGetOneCompanyQuery} from '~/src/store/apis/company-api'
import {useSignoutMutation} from '~/src/store/apis/auth-api'
import {useCurrentUser} from '~/src/store/slice-hooks'
import {useAppDispatch, useAppSelector} from '~/src/store/store-hooks'
import type {INavMenuItem} from '../../layout/MenuItems'
import GlobalDropdown from './GlobalDropdown'
import staffologyLogo from '~/src/assets/images/staffology-logo.svg'
import NavMenuItem from './NavMenuItem'
import SelectAgencyMenu from './SelectAgencyMenu'
import SubMenu from './SubMenu'
import {useActiveLink} from './useActiveLink'
import GlobalSearch from './GlobalSearch'
import NotificationBell from './NotificationBell'
import HelpIconButton from '../HelpIconButton'
import UserMenu from './UserMenu'
import ChangePasswordDialog from '../dialog/ChangePasswordDialog'
import EulaDialog from './EulaDialog'
import {Tooltip} from '~/src/design-system/BaseComponents'
import {useFilteredMenuItems} from '~/src/hooks/useFilteredMenuItems'
import {usePermission} from '~/src/hooks/usePermission'
import {
  APP_NAV_HEIGHT,
  APP_PAGE_SIDE,
  APP_PAGE_SIDE_NARROW,
  APP_PAGE_SIDE_NARROW_MAX,
} from './navMetrics'

interface NavMenuBarProps {
  items: INavMenuItem[]
  variant?: 'platform' | 'msp' | 'main-agency' | 'agency' | 'company' | 'general-admin'
}

// ==================== STYLED COMPONENTS ====================

const MOBILE_BREAKPOINT = '1000px'

const NavContainer = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: ${APP_NAV_HEIGHT};
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  z-index: 1000;
`

const NavInner = styled.div`
  height: 100%;
  padding: 0 ${APP_PAGE_SIDE};
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: ${APP_PAGE_SIDE_NARROW_MAX}) {
    padding: 0 ${APP_PAGE_SIDE_NARROW};
  }
`

// STFFLG-4229: the Staffology wordmark (the SVG file as supplied), sized by height only so the
// width follows the artwork's aspect ratio. Design feedback: 24px, and 22px at 1180px and below.
const BrandLogo = styled.img`
  display: block;
  height: 1.5rem;
  width: auto;
  flex-shrink: 0;

  @media (max-width: 73.75rem) {
    height: 1.375rem;
  }
`

const LogoContainer = styled.div<{$useBlock: boolean}>`
  display: ${({$useBlock}) => ($useBlock ? 'block' : 'flex')};
  align-items: center;
  gap: var(--space-2);
  font-weight: 500;
  font-size: 1rem;
  color: #334d6e;
  flex-shrink: 0;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    max-width: 150px;
    font-size: 14px;
  }
`

const DesktopNavItems = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: none;
  }
`

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-5);

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    gap: var(--space-3);
  }
`

const MobileSettingsButton = styled.button`
  display: none;
  background: none;
  border: none;
  padding: var(--space-2);
  cursor: pointer;
  color: #334d6e;
  transition: color 0.2s;

  &:hover {
    color: var(--brand-cta);
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

const DesktopUserSection = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: none;
  }
`

const AssignedMspText = styled.div`
  font-size: 11px;
  line-height: 13px;
  color: var(--neutral-600);
  text-align: right;
  margin-left: 69px;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: none;
  }
`

// ==================== DRAWER STYLES ====================

const DrawerOverlay = styled.div<{$isOpen: boolean}>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1999;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? 'visible' : 'hidden')};
  transition: opacity 0.3s, visibility 0.3s;
`

const Drawer = styled.div<{$isOpen: boolean}>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 320px;
  max-width: 85vw;
  background: white;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
  z-index: 2000;
  transform: translateX(${(props) => (props.$isOpen ? '0' : '100%')});
  transition: transform 0.3s ease-in-out;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-5);
  border-bottom: 1px solid #ebebeb;
  flex-shrink: 0;
`

const DrawerTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #334d6e;
  margin: 0;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: var(--space-2);
  cursor: pointer;
  color: #334d6e;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-sm);
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
`

const DrawerContent = styled.div`
  flex: 1;
  padding: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
`

const DrawerSection = styled.div`
  margin-bottom: var(--space-6);
`

const DrawerSectionTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: var(--neutral-600);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: var(--space-3);
  padding: 0 var(--space-3);
`

const DrawerNavItem = styled.button<{$active: boolean; $hasSubMenu: boolean}>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-3);
  background: ${(props) => (props.$active ? 'rgba(3, 169, 252, 0.08)' : 'transparent')};
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  text-align: left;
  font-size: 14px;
  font-weight: ${(props) => (props.$active ? 600 : 400)};
  color: ${(props) => (props.$active ? 'var(--brand-cta)' : '#334d6e')};
  transition: background-color 0.2s, color 0.2s;

  &:hover {
    background-color: rgba(3, 169, 252, 0.04);
    color: var(--brand-cta);
  }
`

const DrawerSubMenu = styled.div<{$isExpanded: boolean}>`
  display: ${(props) => (props.$isExpanded ? 'flex' : 'none')};
  flex-direction: column;
  gap: var(--space-1);
  padding-left: var(--space-6);
  margin-top: var(--space-1);
`

const DrawerSubMenuItem = styled.button<{$active: boolean}>`
  width: 100%;
  display: flex;
  align-items: center;
  padding: 10px var(--space-3);
  background: ${(props) => (props.$active ? 'rgba(3, 169, 252, 0.08)' : 'transparent')};
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  text-align: left;
  font-size: 13px;
  font-weight: ${(props) => (props.$active ? 600 : 400)};
  color: ${(props) => (props.$active ? 'var(--brand-cta)' : '#334d6e')};
  transition: background-color 0.2s, color 0.2s;

  &:hover {
    background-color: rgba(3, 169, 252, 0.04);
    color: var(--brand-cta);
  }
`

const DrawerUserSection = styled.div`
  padding: var(--space-4);
  border-top: 1px solid #ebebeb;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  flex-shrink: 0;
`

const ChevronIcon = styled(ChevronRight)<{$isExpanded: boolean}>`
  transition: transform 0.2s;
  transform: ${(props) => (props.$isExpanded ? 'rotate(90deg)' : 'rotate(0)')};
  flex-shrink: 0;
`

// ==================== COMPONENT ====================

const NavMenuBar = ({items, variant}: NavMenuBarProps) => {
  // Filter menu items based on user permissions
  const filteredItems = useFilteredMenuItems(items)
  const {activeLink, handleSetActive, subMenuItems} = useActiveLink(filteredItems)
  const {currentUser} = useCurrentUser()
  const {companyId, roles} = currentUser || {companyId: '', roles: [] as string[]}
  const company = useGetOneCompanyQuery(companyId, {
    skip: !companyId,
  })
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [signout] = useSignoutMutation()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [expandedItems, setExpandedItems] = useState<Record<number, boolean>>({})
  const [isEulaOpen, setIsEulaOpen] = useState(false)
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false)

  const foundItem = filteredItems.find((item) => {
    let val = false
    if (item.link) {
      const pattern = item.activePattern || item.link
      val = location.pathname.search(new RegExp(pattern, 'i')) !== -1
    } else {
      const foundSubItem = item.subMenuItems.find((subItem) => {
        const subPattern = subItem.activePattern || (subItem.link as string)
        const subVal = location.pathname.search(new RegExp(subPattern, 'i')) !== -1
        return subVal
      })
      val = foundSubItem !== undefined
    }
    return val
  })

  const {backendRolePermissions} = useAppSelector((state) => state.root)
  const isCompanyUser = backendRolePermissions?.category === 'Company'

  const agency = useGetOneAgencyQuery(company.currentData?.agencyId ?? '', {
    skip: !company.currentData?.agencyId || isCompanyUser,
  })

  const ref = useRef<HTMLElement>(null)

  // Determine if current URL matches a parent with sub-items (should keep sub-menu open)
  const activeParentWithSubMenu = filteredItems.find((item) => {
    if (!item.subMenuItems || item.subMenuItems.length === 0) return false
    return item.subMenuItems.some((subItem) => {
      const subPattern = subItem.activePattern || (subItem.link as string)
      return location.pathname.search(new RegExp(subPattern, 'i')) !== -1
    })
  })

  // Get the sub-menu items for the active parent
  const activeSubMenuItems = activeParentWithSubMenu?.subMenuItems || []

  // Track manually opened sub-menu (for clicking different parent)
  const [manuallyOpenedParentId, setManuallyOpenedParentId] = useState<number | null>(null)

  // Sub-menu is open if: URL matches a parent with sub-items OR user manually clicked a parent
  const isSubMenuOpen = activeParentWithSubMenu !== undefined || manuallyOpenedParentId !== null

  // Determine which sub-items to show
  const displaySubMenuItems =
    manuallyOpenedParentId !== null
      ? filteredItems.find((item) => item.id === manuallyOpenedParentId)?.subMenuItems || []
      : activeSubMenuItems

  // Close drawer on route change
  useEffect(() => {
    setIsDrawerOpen(false)
    // Clear manually opened parent when navigating (URL-based will take over)
    setManuallyOpenedParentId(null)
  }, [location.pathname])

  // Close submenu when clicking outside (desktop only)
  useEffect(() => {
    const checkIfClickedOutside = (e: MouseEvent) => {
      if (isSubMenuOpen && ref.current && !ref.current.contains(e.target as Node)) {
        setManuallyOpenedParentId(null)
      }
    }

    document.addEventListener('mousedown', checkIfClickedOutside)
    return () => {
      document.removeEventListener('mousedown', checkIfClickedOutside)
    }
  }, [isSubMenuOpen])

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    const id = parseInt(event.currentTarget.id, 10)
    const clickedItem = filteredItems.find((item) => item.id === id)

    handleSetActive(event)

    // If clicked item has sub-menu, open it
    if (clickedItem?.subMenuItems && clickedItem.subMenuItems.length > 0) {
      setManuallyOpenedParentId(id)
    } else {
      setManuallyOpenedParentId(null)
    }
  }

  const handleSubMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    // When clicking a sub-menu item, just navigate - don't close the sub-menu
    handleSetActive(event)
    // Clear manual override so URL-based detection takes over
    setManuallyOpenedParentId(null)
  }

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen)
  }

  const toggleExpandItem = (itemId: number) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }))
  }

  const handleSignOut = async () => {
    if (!currentUser) return
    await signout({email: currentUser.emailAddress}).unwrap()
    navigate('/signin')
  }

  const handleOpenEula = useCallback(() => {
    setIsDrawerOpen(false)
    setIsEulaOpen(true)
  }, [])

  const handleCloseEula = useCallback(() => {
    setIsEulaOpen(false)
  }, [])

  const handleOpenChangePassword = useCallback(() => {
    console.log('handleOpenChangePassword')
    setIsDrawerOpen(false)
    setIsChangePasswordOpen(true)
  }, [])

  const handleCloseChangePassword = useCallback(() => {
    setIsChangePasswordOpen(false)
  }, [])

  const handleDrawerNavClick = (item: INavMenuItem) => {
    if (item.subMenuItems && item.subMenuItems.length > 0) {
      toggleExpandItem(item.id)
    } else {
      // Navigate to the link
      window.location.href = item.link as string
    }
  }

  const handleDrawerSubNavClick = (link: string) => {
    window.location.href = link
  }

  // Check if user has global module permission (for showing Global dropdown)
  const globalPermission = usePermission('global')
  const hasGlobalAccess = globalPermission.canView

  // Determine variant-specific flags
  const isPlatformVariant = variant === 'platform'
  const isGeneralAdmin = variant === 'general-admin' && roles?.includes('general_admin')
  const isCandidateUser = roles?.some((role) => role === 'candidate' || role === 'ollicandidate')

  const roleCategory = backendRolePermissions?.category || backendRolePermissions?.application

  // Determine if user should see agency switcher:
  // Only show for MSP and Agency category roles (not for general_admin, Platform, or Company)
  // User must also have multiple agency assignments
  const isMspOrAgencyCategory =
    roleCategory?.toLowerCase() === 'msp' || roleCategory?.toLowerCase() === 'agency'
  const hasMultipleAgencies =
    isMspOrAgencyCategory &&
    currentUser?.agencyAssignments &&
    currentUser.agencyAssignments.length > 1

  return (
    <>
      <NavContainer ref={ref} data-testid="nav-menu-bar">
        <NavInner data-testid="nav-menu-bar-inner">
          {/* Logo / Company Name */}
          <LogoContainer $useBlock={company?.currentData?.name}>
            {company.currentData?.name ? (
              <Tooltip content={company.currentData.name} side="bottom">
                <span>{company.currentData.name}</span>
              </Tooltip>
            ) : (
              <BrandLogo
                src={staffologyLogo}
                alt="Staffology"
                data-testid="nav-menu-bar-brand-logo"
              />
            )}
          </LogoContainer>

          {/* Desktop Navigation Items */}
          <DesktopNavItems data-testid="nav-menu-bar-desktop-items">
            {filteredItems?.map((page, index) => {
              const hasSubMenu = page.subMenuItems && page.subMenuItems.length > 0
              const firstSubMenuLink =
                hasSubMenu && page.subMenuItems ? page.subMenuItems[0].link : undefined
              const subMenuLinks =
                hasSubMenu && page.subMenuItems
                  ? page.subMenuItems.map((item) => item.link ?? '')
                  : []

              return (
                <NavMenuItem
                  id={page.id.toString()}
                  key={page.id}
                  text={page.text}
                  link={page.link ?? location.pathname}
                  active={page.id === foundItem?.id}
                  handleSetActive={handleClick}
                  hasSubMenu={hasSubMenu}
                  firstSubMenuLink={firstSubMenuLink}
                  subMenuLinks={subMenuLinks}
                />
              )
            })}
            {(isGeneralAdmin || (isPlatformVariant && hasGlobalAccess)) && (
              <div style={{marginLeft: 'var(--space-2)'}}>
                <GlobalDropdown
                  active={
                    location.pathname.includes('/global') ||
                    location.pathname.includes('/roles-permissions')
                  }
                />
              </div>
            )}
          </DesktopNavItems>

          {/* Right Section */}
          <RightSection data-testid="nav-menu-bar-right-section">
            {/* Desktop User Section */}
            <DesktopUserSection>
              {hasMultipleAgencies && (
                <div style={{marginRight: 'var(--space-5)'}}>
                  <SelectAgencyMenu />
                </div>
              )}
              {!isCandidateUser && (
                <>
                  <GlobalSearch />
                  <HelpIconButton />
                </>
              )}
              {variant !== 'company' && <NotificationBell variant={variant} />}
              {currentUser && <UserMenu currentUser={currentUser} />}
              {agency.currentData && (
                <AssignedMspText>
                  Assigned MSP <br />
                  {agency.currentData.name}
                </AssignedMspText>
              )}
            </DesktopUserSection>

            {/* Mobile Settings Button */}
            <MobileSettingsButton
              onClick={toggleDrawer}
              aria-label="Open settings menu"
              data-testid="nav-menu-bar-mobile-settings-button">
              <Settings size={24} />
            </MobileSettingsButton>
          </RightSection>
        </NavInner>

        {/* Desktop SubMenu - stays open when URL matches a sub-item */}
        {isSubMenuOpen && displaySubMenuItems.length > 0 ? (
          <SubMenu
            items={displaySubMenuItems}
            closeMenu={handleSubMenuClick}
            activeSubLink={activeLink.subMenuId}
            currentPath={location.pathname}
          />
        ) : null}
      </NavContainer>

      {/* Mobile Drawer */}
      <DrawerOverlay
        $isOpen={isDrawerOpen}
        onClick={toggleDrawer}
        data-testid="nav-menu-bar-drawer-overlay"
      />
      <Drawer $isOpen={isDrawerOpen} data-testid="nav-menu-bar-drawer">
        <DrawerHeader data-testid="nav-menu-bar-drawer-header">
          <DrawerTitle>Menu</DrawerTitle>
          <CloseButton
            onClick={toggleDrawer}
            aria-label="Close menu"
            data-testid="nav-menu-bar-drawer-close-button">
            <X size={20} />
          </CloseButton>
        </DrawerHeader>

        <DrawerContent data-testid="nav-menu-bar-drawer-content">
          {/* Navigation Section */}
          <DrawerSection data-testid="nav-menu-bar-drawer-nav-section">
            <DrawerSectionTitle>Navigation</DrawerSectionTitle>
            {filteredItems?.map((page, index) => {
              const hasSubMenu = page.subMenuItems && page.subMenuItems.length > 0
              const isActive = page.id === foundItem?.id
              const isExpanded = expandedItems[page.id] || false

              // Add Global dropdown for platform variant with global access
              if (isPlatformVariant && hasGlobalAccess && index === 3) {
                return (
                  <React.Fragment key={`global-drawer-${page.id}`}>
                    <DrawerNavItem
                      $active={
                        location.pathname.includes('/global') ||
                        location.pathname.includes('/roles-permissions')
                      }
                      $hasSubMenu={false}
                      onClick={() => (window.location.href = '/global')}>
                      Global
                    </DrawerNavItem>
                    <DrawerNavItem
                      $active={isActive}
                      $hasSubMenu={hasSubMenu}
                      onClick={() => handleDrawerNavClick(page)}>
                      {page.text}
                      {hasSubMenu && <ChevronIcon size={16} $isExpanded={isExpanded} />}
                    </DrawerNavItem>
                    {hasSubMenu && (
                      <DrawerSubMenu $isExpanded={isExpanded}>
                        {page.subMenuItems?.map((subItem) => {
                          const subPattern = subItem.activePattern || (subItem.link as string)
                          const isSubActive =
                            location.pathname.search(new RegExp(subPattern, 'i')) !== -1

                          return (
                            <DrawerSubMenuItem
                              key={subItem.id}
                              $active={isSubActive}
                              onClick={() => handleDrawerSubNavClick(subItem.link as string)}>
                              {subItem.text}
                            </DrawerSubMenuItem>
                          )
                        })}
                      </DrawerSubMenu>
                    )}
                  </React.Fragment>
                )
              }

              return (
                <React.Fragment key={page.id}>
                  <DrawerNavItem
                    $active={isActive}
                    $hasSubMenu={hasSubMenu}
                    onClick={() => handleDrawerNavClick(page)}>
                    {page.text}
                    {hasSubMenu && <ChevronIcon size={16} $isExpanded={true} />}
                  </DrawerNavItem>
                  {hasSubMenu && (
                    <DrawerSubMenu $isExpanded={true}>
                      {page.subMenuItems?.map((subItem) => {
                        const subPattern = subItem.activePattern || (subItem.link as string)
                        const isSubActive =
                          location.pathname.search(new RegExp(subPattern, 'i')) !== -1

                        return (
                          <DrawerSubMenuItem
                            key={subItem.id}
                            $active={isSubActive}
                            onClick={() => handleDrawerSubNavClick(subItem.link as string)}>
                            {subItem.text}
                          </DrawerSubMenuItem>
                        )
                      })}
                    </DrawerSubMenu>
                  )}
                </React.Fragment>
              )
            })}
            {/* Add Global menu item for general_admin in mobile drawer */}
            {isGeneralAdmin && (
              <DrawerNavItem
                $active={location.pathname.includes('/roles-permissions')}
                $hasSubMenu={false}
                onClick={() => (window.location.href = '/roles-permissions')}>
                Global
              </DrawerNavItem>
            )}
          </DrawerSection>

          {/* Settings Section */}
          <DrawerSection data-testid="nav-menu-bar-drawer-settings-section">
            <DrawerSectionTitle>Settings</DrawerSectionTitle>

            {/* User Info */}
            {currentUser && (
              <DrawerNavItem $active={false} $hasSubMenu={false} style={{cursor: 'default'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: 'var(--space-2)'}}>
                  <User size={16} />
                  <span>{currentUser.emailAddress}</span>
                </div>
              </DrawerNavItem>
            )}

            {/* Change Password */}
            <DrawerNavItem
              $active={false}
              $hasSubMenu={false}
              onClick={handleOpenChangePassword}
              data-testid="nav-menu-bar-drawer-change-password">
              <div style={{display: 'flex', alignItems: 'center', gap: 'var(--space-2)'}}>
                <Lock size={16} />
                <span>Change Password</span>
              </div>
            </DrawerNavItem>

            {/* EULA */}
            <DrawerNavItem
              $active={false}
              $hasSubMenu={false}
              onClick={handleOpenEula}
              data-testid="nav-menu-bar-drawer-eula">
              <div style={{display: 'flex', alignItems: 'center', gap: 'var(--space-2)'}}>
                <FileText size={16} />
                <span>Eula</span>
              </div>
            </DrawerNavItem>

            {/* Logout */}
            <DrawerNavItem
              $active={false}
              $hasSubMenu={false}
              onClick={() => void handleSignOut()}
              data-testid="nav-menu-bar-drawer-logout">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  color: '#dc2626',
                }}>
                <LogOut size={16} />
                <span>Log Out</span>
              </div>
            </DrawerNavItem>
          </DrawerSection>
        </DrawerContent>

        {/* Drawer Footer - Agency Info */}
        <DrawerUserSection>
          {hasMultipleAgencies && <SelectAgencyMenu />}
          {agency.currentData && (
            <div style={{fontSize: '11px', color: 'var(--neutral-600)', textAlign: 'center'}}>
              Assigned MSP: {agency.currentData.name}
            </div>
          )}
        </DrawerUserSection>
      </Drawer>

      {/* Dialogs */}
      {currentUser && (
        <>
          <ChangePasswordDialog
            isOpen={isChangePasswordOpen}
            handleCancel={handleCloseChangePassword}
          />
          <EulaDialog isModalOpen={isEulaOpen} onModalClose={handleCloseEula} />
        </>
      )}
    </>
  )
}

export default NavMenuBar
